import {
  FieldElementUnion,
  FormData,
  FormRepeatableUnitValue,
  FormSchema,
  IFormSchemaV2,
  isFileInputElement,
  isInputElement,
  RefkeyValueType,
} from "@graffer-inc/form-schema";

import { ElementFieldMapping } from "../kintone-export-setting";

export type BulkExtractMappedRefKeysProps = Record<
  ExtractMappedRefKeysProps["submissionId"],
  ExtractMappedRefKeysProps
>;

export type ExtractMappedRefKeysProps = {
  submissionId: string;
  formSchema: IFormSchemaV2;
  document: FormData;
  elementFieldMappings: ElementFieldMapping[];
};

export type BulkExtractMappedRefKeysResult = Record<
  ExtractMappedRefKeysProps["submissionId"],
  ExtractMappedRefKeysResult
>;

export type ExtractMappedRefKeysResult = {
  mappedFileRefKeys: RefkeyValueType[];
};

export const bulkExtractMappedRefKeys = (
  arg: BulkExtractMappedRefKeysProps,
): BulkExtractMappedRefKeysResult => {
  const result: BulkExtractMappedRefKeysResult = {};

  Object.entries(arg).forEach(([submissionId, props]) => {
    result[submissionId] = extractMappedRefKeys(props);
  });

  return result;
};

export const extractMappedRefKeys = ({
  formSchema,
  document,
  elementFieldMappings,
}: ExtractMappedRefKeysProps): ExtractMappedRefKeysResult => {
  const mappedFileRefKeys: RefkeyValueType[] = [];

  const elements = FormSchema.fromJSON(formSchema)
    .flattenDeep()
    .reduce<Record<FieldElementUnion["id"], FieldElementUnion>>(
      (prev, curr) => {
        prev[curr.id] = curr;
        return prev;
      },
      {},
    );

  elementFieldMappings.forEach(({ formElementId }) => {
    const element = elements[formElementId];

    // elementFieldMappings が validateElementFieldMappings を
    // パスしていれば input element にしかならない。
    if (!isInputElement(element)) {
      throw new Error(
        `element is not input element: ${element.id}, ${element.elementType}`,
      );
    }

    if (!element.parent) {
      const value = document[element.id];

      // 任意項目の場合は値が存在しないことがある
      if (!value) {
        return;
      }

      if (isFileInputElement(element)) {
        mappedFileRefKeys.push(value as RefkeyValueType);
      }

      return;
    }

    const repeatableElementId = element.parent.repeatable.id;

    const repeatableValue = document[repeatableElementId] as
      | FormRepeatableUnitValue[]
      | undefined;

    // 任意項目の場合は値が存在しないことがある
    if (!repeatableValue) {
      return;
    }

    repeatableValue.forEach((unitValue) => {
      const value = unitValue[element.id];
      // 任意項目の場合は値が存在しないことがある
      if (!value) {
        return;
      }
      if (isFileInputElement(element)) {
        mappedFileRefKeys.push(value as RefkeyValueType);
      }
    });
  });

  return {
    mappedFileRefKeys,
  };
};
